'use client';

import Image from 'components/commercetools-ui/atoms/image';
import { WidgetPillProps } from '../../types';
import { interpolateCount } from '../../utils';

/**
 * Fixed bottom-left launcher for the My Photos widget. Sits just below the
 * overlay layer (z-600) so any overlay-backed surface — this widget's own
 * drawer, the cart drawer, modals — covers it instead of the pill floating on
 * top of them. Collapses to just the
 * icon + count badge and expands (animated) to reveal the "photos brought with
 * you" copy. Clicking opens the gallery modal.
 */
const WidgetPill = ({
  count,
  icon,
  expanded,
  onOpen,
  pillHeading,
  pillSubheading,
  pillBgColor,
  pillHeadingTextColor,
  pillSubheadingTextColor,
  badgeBgColor,
  badgeTextColor,
  pillHeadingTextFont,
  pillSubheadingTextFont,
  pillHeadingTextFontWeight,
  pillSubheadingTextFontWeight,
}: WidgetPillProps) => {
  const fontWeightByStyle = {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  } as const;

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={interpolateCount(pillHeading, count)}
      className={`fixed bottom-20 left-20 z-[590] flex h-50 min-w-50 items-center rounded-full bg-white shadow-[0px_4px_16px_0px_#00000026] transition-[box-shadow,padding,width] duration-300 hover:shadow-[0px_6px_20px_0px_#00000033] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#018463] ${
        expanded ? 'p-8 pr-20' : 'w-50 justify-center p-0'
      }`}
      style={{ backgroundColor: pillBgColor }}
      data-ffid="btn-my-photos-widget-open"
      data-state={expanded ? 'expanded' : 'collapsed'}
    >
      <span className="relative inline-flex size-29 shrink-0 items-center justify-center rounded-full border-[0.72px] border-[#D6D6D6] bg-white shadow-[0px_2.94px_7.35px_0px_rgba(0,0,0,0.15)]">
        {icon?.media || icon?.src ? (
          <Image {...icon} width={18} height={18} className="size-18 rounded-full object-contain" />
        ) : (
          <span className="size-32 rounded-full bg-neutral-200" aria-hidden="true" />
        )}
        {count > 0 && (
          <span
            className="absolute -right-4 -top-4 inline-flex h-15 min-w-15 items-center justify-center rounded-full px-5 text-10 font-bold leading-none"
            style={{ backgroundColor: badgeBgColor, color: badgeTextColor }}
            data-ffid="lbl-my-photos-widget-badge"
          >
            {count}
          </span>
        )}
      </span>

      <span
        className={`flex flex-col items-start gap-4 overflow-hidden whitespace-nowrap text-left transition-all duration-300 ${
          expanded ? 'ml-12 max-w-260 opacity-100' : 'max-w-0 opacity-0'
        }`}
        aria-hidden={!expanded}
      >
        <span
          className="text-14 font-medium leading-tight text-[#333333]"
          style={{
            color: pillHeadingTextColor,
            fontSize: pillHeadingTextFont ? `${pillHeadingTextFont}px` : undefined,
            fontWeight: pillHeadingTextFontWeight ? fontWeightByStyle[pillHeadingTextFontWeight] : undefined,
          }}
          data-ffid="lbl-my-photos-widget-pill-label"
        >
          {interpolateCount(pillHeading, count)}
        </span>
        {pillSubheading && (
          <span
            className="text-12 leading-tight text-[#5C5C5C]"
            style={{
              color: pillSubheadingTextColor,
              fontSize: pillSubheadingTextFont ? `${pillSubheadingTextFont}px` : undefined,
              fontWeight: pillSubheadingTextFontWeight ? fontWeightByStyle[pillSubheadingTextFontWeight] : undefined,
            }}
            data-ffid="lbl-my-photos-widget-pill-sublabel"
          >
            {pillSubheading}
          </span>
        )}
      </span>
    </button>
  );
};

export default WidgetPill;
